import { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleDollarSign, Pencil } from 'lucide-react';
import Alert from '../components/common/Alert';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Input from '../components/forms/Input';
import Select from '../components/forms/Select';
import Textarea from '../components/forms/Textarea';
import Table from '../components/tables/Table';
import { financeService } from '../services';

const statuses = [
  { value: 'PLANNED', label: 'Planificado' },
  { value: 'APPROVED', label: 'Aprobado' },
  { value: 'DELIVERED', label: 'Entregado' },
  { value: 'CANCELLED', label: 'Cancelado' },
];
const formatMoney = (value) => `${Number(value || 0).toLocaleString('es-PY')} Gs.`;

function FinancePage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ amount: '', concept: '', notes: '', status: 'PLANNED' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [listResponse, summaryResponse] = await Promise.all([
        financeService.getAll({ search, page_size: 100 }),
        financeService.getSummary(),
      ]);
      setRows(listResponse.data);
      setSummary(summaryResponse.data);
    } catch (requestError) {
      setError(requestError.message || 'No fue posible cargar la información financiera.');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      amount: row.amount || '', concept: row.concept || '',
      notes: row.notes || '', status: row.status || 'PLANNED',
    });
  };

  const save = async () => {
    if (form.amount === '' || Number(form.amount) < 0) {
      setError('Ingresa un monto válido mayor o igual a cero.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await financeService.updateAllocation(editing.voter_id, {
        ...form, amount: Number(form.amount),
      });
      setEditing(null);
      setNotice('Asignación financiera actualizada correctamente.');
      await load();
    } catch (requestError) {
      setError(requestError.message || 'No fue posible guardar la asignación.');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    { key: 'document', header: 'Cédula' },
    { key: 'voter_name', header: 'Votante' },
    { key: 'coordinator_name', header: 'Coordinador' },
    { key: 'leader_name', header: 'Puntero' },
    { key: 'amount', header: 'Monto', align: 'right', render: formatMoney },
    { key: 'status', header: 'Estado', render: (value) => statuses.find((item) => item.value === value)?.label || value },
    { key: 'actions', header: 'Acciones', render: (_, row) => (
      <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
        <Pencil className="size-4" /> Asignar
      </Button>
    ) },
  ], []);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold text-primary-700">Planificación monetaria</p>
        <h1 className="mt-1 text-2xl font-bold text-content-primary">Financiero</h1>
        <p className="mt-1 text-sm text-content-muted">Asigna y consolida los fondos destinados a votantes clasificados como Verde.</p>
      </div>
      {error && <Alert variant="danger" title="No se pudo completar la operación" onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert variant="success" title="Operación completada" onClose={() => setNotice('')}>{notice}</Alert>}
      {summary && <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card><p className="text-sm text-content-muted">Votantes verdes</p><p className="mt-2 text-2xl font-bold">{summary.totals.black_voters}</p></Card>
        <Card><p className="text-sm text-content-muted">Con asignación</p><p className="mt-2 text-2xl font-bold">{summary.totals.allocated_voters}</p></Card>
        <Card><p className="text-sm text-content-muted">Pendientes</p><p className="mt-2 text-2xl font-bold">{summary.totals.pending_voters}</p></Card>
        <Card><p className="text-sm text-content-muted">Total planificado</p><p className="mt-2 text-2xl font-bold text-primary-700">{formatMoney(summary.totals.amount)}</p></Card>
      </div>}
      <Card title="Votantes y asignaciones" description="Los punteros solo determinan la clasificación; los montos se administran aquí." action={<Input id="finance-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar votante o cédula" />}>
        {loading ? <div className="grid min-h-48 place-items-center"><LoadingSpinner label="Cargando finanzas" /></div>
          : <Table columns={columns} data={rows} rowKey="voter_id" emptyMessage="No existen votantes verdes dentro de tu estructura." />}
      </Card>
      {summary && <div className="grid gap-5 xl:grid-cols-2">
        <Card title="Resumen por coordinador" description="Monto consolidado que corresponde a cada coordinación.">
          <Table rowKey="id" columns={[
            { key: 'name', header: 'Coordinador' }, { key: 'voters', header: 'Votantes' },
            { key: 'amount', header: 'Total', align: 'right', render: formatMoney },
          ]} data={summary.by_coordinator} emptyMessage="Todavía no hay asignaciones." />
        </Card>
        <Card title="Resumen por puntero" description="Detalle para distribuir fondos dentro de cada coordinación.">
          <Table rowKey="id" columns={[
            { key: 'name', header: 'Puntero' }, { key: 'coordinator', header: 'Coordinador' },
            { key: 'voters', header: 'Votantes' }, { key: 'amount', header: 'Total', align: 'right', render: formatMoney },
          ]} data={summary.by_leader} emptyMessage="Todavía no hay asignaciones." />
        </Card>
      </div>}
      <Modal open={Boolean(editing)} onClose={() => !saving && setEditing(null)} title="Asignar fondos" description={editing ? `${editing.voter_name} · ${editing.document}` : ''} footer={<><Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button><Button onClick={save} loading={saving}><CircleDollarSign className="size-4" /> Guardar asignación</Button></>}>
        <div className="space-y-4">
          <Input id="finance-amount" type="number" min="0" step="1" label="Monto en guaraníes" value={form.amount} onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))} required />
          <Input id="finance-concept" label="Concepto" value={form.concept} onChange={(event) => setForm((current) => ({ ...current, concept: event.target.value }))} placeholder="Ej. Apoyo logístico" />
          <Select id="finance-status" label="Estado" value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))} options={statuses} />
          <Textarea id="finance-notes" label="Observaciones" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} />
        </div>
      </Modal>
    </div>
  );
}

export default FinancePage;
